import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';
import { DEFAULT_TITLE } from 'constants/common';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { businessPlanQueryKeys } from 'services/businessPlan/businessPlan-qk';
import { useUpdateBusinessPlanChapter } from 'services/businessToolForm/hooks/useUpdateChapter';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessPlan,
  getSetBusinessPlanChapter,
  getSetBusinessToolTitle,
} from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useDebouncedSave } from 'hooks/useDebounceSave';
import { useExamplePreview } from 'hooks/useExamplePreview';
import { useIsBusinessPlanOwner } from 'hooks/useIsBusinessPlanOwner';

import { ISectionFormData } from 'interfaces/businessTool';

import { FUNDING_DIRECTIONS_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/FundingDirectionsForm/utils/form';
import { LOAN_REPAYMENT_SCHEDULE_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/LoanRepaymentScheduleForm/utils/form';

import { asyncDelay } from 'helpers/delay';
import { getVariable } from 'helpers/environment.utils';
import { getLangName } from 'helpers/getBusinessDocumentLanguage';
import ToastService from 'helpers/ToastService';

import { ISectionChangeObserverProps } from './types';

const STORE_VALUE_SAVE_FREQUENCY_MS = 300;

const FUNDING_DIRECTIONS_ENGLISH_VARIANTS = [
  'R&D',
  'Marketing and Sales',
  'Working Capital',
  'Construction',
  'Equipment Purchase',
  'Other',
];

const LOAN_REPAYMENT_SCHEDULE_ENGLISH_VARIANTS = ['Repayment of loan', 'Interest payment'];

const normalizeChapterContent = <T,>(chapterName: string, content: T): T => {
  if (chapterName !== BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN || !content || typeof content !== 'object') {
    return content;
  }

  const financialPlanContent = content as Record<string, any>;
  const cashFlowElements = financialPlanContent[BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.CASH_FLOW_ELEMENTS];
  const fundingDirections = cashFlowElements?.fundingDirections;
  const loanRepaymentSchedule = cashFlowElements?.loanRepaymentSchedule;

  if (!cashFlowElements || (!loanRepaymentSchedule && !fundingDirections)) {
    return content;
  }

  return {
    ...financialPlanContent,
    [BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.CASH_FLOW_ELEMENTS]: {
      ...cashFlowElements,
      ...(fundingDirections
        ? {
            fundingDirections: {
              ...fundingDirections,
              [FUNDING_DIRECTIONS_NAMES.VARIANTS]: [...FUNDING_DIRECTIONS_ENGLISH_VARIANTS],
            },
          }
        : {}),
      ...(loanRepaymentSchedule
        ? {
            loanRepaymentSchedule: {
              ...loanRepaymentSchedule,
              [LOAN_REPAYMENT_SCHEDULE_NAMES.VARIANTS]: [...LOAN_REPAYMENT_SCHEDULE_ENGLISH_VARIANTS],
            },
          }
        : {}),
    },
  } as T;
};

export const SectionChangeObserver: React.FC<ISectionChangeObserverProps> = memo(({ chapterName }) => {
  const { id: businessPlanId = '' } = useParams();
  const { viewonly } = useBusinessToolStore(getBusinessPlan);
  const { t } = useTranslation();

  const authUser = useUserStore(getAuthUser);
  const { isAuthenticated } = useAuthenticated();
  const { isExamplePage } = useExamplePreview();
  const isBusinessPlanOwner = useIsBusinessPlanOwner();

  const { getValues } = useForm();
  const sectionValues = useWatch({ name: chapterName });

  const queryClient = useQueryClient();

  const getDelayTime = () => {
    switch (chapterName) {
      case BUSINESS_TOOL_SECTIONS.ABOUT_COMPANY:
        return 50;
      case BUSINESS_TOOL_SECTIONS.BUSINESS_OPERATIONS:
        return 200;
      case BUSINESS_TOOL_SECTIONS.EXECUTIVE_SUMMARY:
        return 300;
      case BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN:
        return 10;
      case BUSINESS_TOOL_SECTIONS.GLOBAL:
        return 10;
      case BUSINESS_TOOL_SECTIONS.IMPLEMENTATION_PLAN:
        return 50;
      case BUSINESS_TOOL_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS:
        return 400;
      case BUSINESS_TOOL_SECTIONS.MANAGEMENT_AND_ORGANIZATION:
        return 500;
      case BUSINESS_TOOL_SECTIONS.MARKETING_AND_SALES:
        return 900;
      case BUSINESS_TOOL_SECTIONS.MARKET_ANALYSIS:
        return 650;
      case BUSINESS_TOOL_SECTIONS.OVERVIEW:
        return 600;
      case BUSINESS_TOOL_SECTIONS.PRODUCTS_AND_SERVICES:
        return 800;
      case BUSINESS_TOOL_SECTIONS.RAISING_AND_ALLOCATING_FUNDS:
        return 700;
      default:
        return 10;
    }
  };

  const mutation = useUpdateBusinessPlanChapter();

  const apiPrev = useRef<ISectionFormData<typeof chapterName>>(sectionValues);
  const storePrev = useRef<ISectionFormData<typeof chapterName>>(sectionValues);

  const setBusinessPlanChapter = useBusinessToolStore(getSetBusinessPlanChapter);
  const setBusinessToolTitle = useBusinessToolStore(getSetBusinessToolTitle);

  const apiSaveFormSectionData = useCallback(
    async (content: ISectionFormData<typeof chapterName>) => {
      const updateDocAndBusinessToolTitles = (content: ISectionFormData<typeof chapterName>) => {
        const { companyName } = content as ISectionFormData<typeof BUSINESS_TOOL_SECTIONS.GLOBAL>;
        const { companyName: prevCompanyName } = apiPrev.current as ISectionFormData<
          typeof BUSINESS_TOOL_SECTIONS.GLOBAL
        >;

        if (companyName !== prevCompanyName) {
          document.title = companyName || DEFAULT_TITLE;
          setBusinessToolTitle(companyName);
        }
      };
      await asyncDelay(getDelayTime());

      const isChangeDetected = !isEqual(content, apiPrev.current);

      if (!authUser || !isAuthenticated) {
        if (isChangeDetected && chapterName === BUSINESS_TOOL_SECTIONS.GLOBAL) {
          updateDocAndBusinessToolTitles(content);
        }

        return;
      }

      if (apiPrev.current && isChangeDetected) {
        if (getVariable('NODE_ENV') === 'development') {
          // eslint-disable-next-line no-console
          console.log(`Saving data ${chapterName}:`, content);
        }
        const language = localStorage.getItem('language');
        const documentLanguage = getLangName(language ?? '');
        const currentValues = getValues(chapterName);
        let updatedValues = merge(currentValues, content);
        if (chapterName === 'GLOBAL') {
          updatedValues = merge(updatedValues, { documentLanguage });
        }
        const normalizedUpdatedValues = normalizeChapterContent(chapterName, updatedValues);
        const body = { chapterName, content: JSON.stringify(normalizedUpdatedValues) };
        await mutation.mutateAsync({ id: businessPlanId, body });

        queryClient.invalidateQueries({ queryKey: businessPlanQueryKeys.businessPlan(businessPlanId) });

        apiPrev.current = content;
      }
    },
    [businessPlanId, chapterName, isExamplePage],
  );

  // Save the value in the storage more often so that we can use this data to generate PDF previews
  const storeSaveFormSectionData = useCallback(
    (content: ISectionFormData<typeof chapterName>) => {
      if (storePrev.current && !isEqual(content, storePrev.current)) {
        const normalizedContent = normalizeChapterContent(chapterName, content);

        setBusinessPlanChapter(chapterName, normalizedContent);

        if (!authUser || !isAuthenticated) {
          if (localStorage.getItem('unauthorizedBusinessToolStore')) {
            const prevData = JSON.parse(localStorage.getItem('unauthorizedBusinessToolStore')!);
            const updatedData = { ...prevData, [chapterName]: normalizedContent };
            const unauthorizedBusinessToolStore = JSON.stringify(updatedData);
            localStorage.setItem('unauthorizedBusinessToolStore', unauthorizedBusinessToolStore);
          } else {
            localStorage.setItem('unauthorizedBusinessToolStore', JSON.stringify({ [chapterName]: normalizedContent }));
          }
        }

        storePrev.current = content;
      }
    },
    [chapterName, setBusinessPlanChapter],
  );

  useDebouncedSave({ data: sectionValues, saveFunction: apiSaveFormSectionData });
  useDebouncedSave({
    data: sectionValues,
    saveFunction: storeSaveFormSectionData,
    debounceTime: STORE_VALUE_SAVE_FREQUENCY_MS,
  });

  // Error handle
  useEffect(() => {
    const errorMessageTitle = viewonly ? t('readOnlyMode') : t('failedToSave');
    const errorMessageSubtitle = viewonly ? t('upgradeToEdit') : t('changesWillBeSavedAfterConnectionRestored');
    if (mutation.failureReason && mutation.failureCount === 1) {
      ToastService.showError(errorMessageSubtitle, errorMessageTitle, {
        autoClose: 3000, // 3s to close toast component
      });
    }
  }, [mutation.failureReason, mutation.failureCount, viewonly]);

  return null;
});
